<template>
  <base-wrapper :loading="loading || loadingJobStatus">
    <div class="space-y-4">
      <div class="flex justify-end gap-2">
        <base-input
          placeholder="Cari email"
          type="search"
          :shadow="false"
          debounce
          v-model="filter.email"
          @native-input="loadMembers"
        />
        <base-input
          placeholder="Cari no. telp"
          type="search"
          :shadow="false"
          debounce
          v-model="filter.cellphone"
          @native-input="loadMembers"
        />
        <base-input
          placeholder="Cari kode atau nama"
          type="search"
          :shadow="false"
          debounce
          v-model="filter.search"
          @native-input="loadMembers"
        />
        <base-button :loading="loadingExport.visible" @click="onExport">
          <Icon
            v-if="!loadingExport.visible"
            icon="heroicons:arrow-down-tray"
            class="h-4 w-4"
          />
          {{ loadingExport.visible ? loadingExport.text : 'Export' }}
        </base-button>
      </div>
      <datatable
        :total="summary.total"
        :perPage="members.meta.page.perPage"
        :currentPage="currentPage"
        :meta="members.meta"
        :columns="[
          { id: 'code', name: 'Kode Kantor' },
          { id: 'upline', name: 'Kode / Nama Upline' },
          { id: 'stockist', name: 'Kode / Nama Stockist' },
          { id: 'sc', name: 'Kode / Nama SC' },
          { id: 'bank_account_number', name: 'No. Rek' },
          { id: 'full_address', name: 'Alamat / Telp' },
          { id: 'status', name: 'Status', theadClass: 'text-center' },
        ]"
        cursor
        @pagechanged="onChangePage"
        @prev="currentPage--"
        @next="currentPage++"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="member in members.data"
            :key="member.id"
            :class="[classes.tr, 'bg-white hover:bg-green-100']"
          >
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onClickDetail(getOffice(member))"
            >
              <p class="font-bold text-gray-900">
                {{ member.attributes.code }}
              </p>
              <span class="text-xs text-gray-600">{{
                member.attributes.name
              }}</span>
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onClickDetail(getOffice(member))"
            >
              <p v-if="!hasUpline(member, 'referred_by')">-</p>
              <template v-else>
                <p class="font-bold text-gray-900">
                  {{ getUpline(member, 'referred_by').code }}
                </p>
                <span class="text-xs text-gray-600">{{
                  getUpline(member, 'referred_by').name
                }}</span>
              </template>
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onClickDetail(getOffice(member))"
            >
              <p v-if="!hasDownline(member, 'promoting_to_stockist')">-</p>
              <template v-else>
                <p class="font-bold text-gray-900">
                  {{ getDownline(member, 'promoting_to_stockist').code }}
                </p>
                <span class="text-xs text-gray-600">{{
                  getDownline(member, 'promoting_to_stockist').name
                }}</span>
              </template>
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onClickDetail(getOffice(member))"
            >
              <p v-if="!hasDownline(member, 'promoting_to_sc')">-</p>
              <template v-else>
                <p class="font-bold text-gray-900">
                  {{ getDownline(member, 'promoting_to_sc').code }}
                </p>
                <span class="text-xs text-gray-600">{{
                  getDownline(member, 'promoting_to_sc').name
                }}</span>
              </template>
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onClickDetail(getOffice(member))"
            >
              {{ getOffice(member).attributes.bank_account_number ?? '-' }}
            </td>
            <td
              :class="[classes.td, 'cursor-pointer']"
              @click="onClickDetail(getOffice(member))"
            >
              <p>
                {{ getOffice(member).attributes.full_address ?? '-' }}
              </p>
              <p class="text-xs">
                {{ getOffice(member).attributes.cellphone ?? '' }}
              </p>
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge
                v-if="!getOffice(member).attributes.is_active"
                color="yellow"
                >Tidak Aktif</base-badge
              >
              <base-badge
                v-else-if="getOffice(member).attributes.is_expired"
                color="red"
                >Expired</base-badge
              >
              <base-badge
                v-else-if="!getOffice(member).attributes.is_promoted"
                color="green"
                class="cursor-pointer"
                @click="onPromote(getOffice(member))"
                >Promosi Ke Stockist</base-badge
              >
              <base-badge v-else color="blue">{{
                getOffice(member).attributes.promoted_by
              }}</base-badge>
            </td>
          </tr>
        </template>
      </datatable>
    </div>

    <view-office-modal
      size="max-w-6xl"
      :grid-column="3"
      office-type="member"
      :visible="detailModal.visible"
      :office-id="detailModal.officeId"
      :columns="['ktp_number', 'upline', 'promoted_stockist', 'promoted_sc']"
      editable
      edit-route="master.member.edit"
      @close="detailModal.visible = false"
    />

    <promote-office-to-stockist-modal
      :visible="promoteModal.visible"
      :office-id="promoteModal.officeId"
      @close="promoteModal.visible = false"
      @promoted="onPromoted"
    />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import ViewOfficeModal from '@/components/office/view-office-modal.vue';
import PromoteOfficeToStockistModal from '@/components/office/promote-office-to-stockist-modal.vue';
import { mapActions, mapGetters } from 'vuex';
import { downloadFileUrl } from '../../../../../services/utils.service';

export default {
  mixins: [requestMixin],
  components: {
    ViewOfficeModal,
    PromoteOfficeToStockistModal,
  },
  data() {
    return {
      currentPage: 1,
      detailModal: {
        officeId: null,
        visible: false,
      },
      promoteModal: {
        officeId: null,
        visible: false,
      },
      filter: {
        search: null,
        email: null,
        cellphone: null,
      },
      loading: false,
      loadingJobStatus: false,
      loadingExport: {
        visible: false,
        text: null,
      },
      members: {
        meta: {
          page: {},
        },
        data: [],
      },
      summary: {},
    };
  },
  computed: {
    ...mapGetters({
      getChannelName: 'auth/getChannelName',
    }),
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    addListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onExportProgress
      );
    },
    getOffice(member) {
      return this.getSingleIncluded(
        this.members,
        member.relationships.office.data.id
      );
    },
    getUpline(member, type) {
      const office = this.getOffice(member);

      return office.attributes.upline_histories.find(
        (history) => history.type === type
      );
    },
    hasUpline(member, type) {
      const office = this.getOffice(member);

      if (!office.attributes.upline_histories) {
        return false;
      }

      return office.attributes.upline_histories.some(
        (history) => history.type === type
      );
    },
    getDownline(member, type) {
      const office = this.getOffice(member);

      return office.attributes.downline_histories.find(
        (history) => history.type === type
      );
    },
    hasDownline(member, type) {
      const office = this.getOffice(member);

      if (!office.attributes.downline_histories) {
        return false;
      }

      return office.attributes.downline_histories.some(
        (history) => history.type === type
      );
    },
    async loadJobStatus() {
      this.loadingJobStatus = true;

      const [res, err] = await this.request(
        '/api/v1/users/-actions/check-job-status',
        {
          params: {
            type: 'export_pin_member',
          },
        }
      );

      if (!err) {
        const { running } = res;

        if (running) {
          this.startLoadingExport('Sedang Diproses');
        }
      }

      this.loadingJobStatus = false;
    },
    async loadMembers(params) {
      this.loading = true;

      const [res, err] = await this.request('/api/v1/offices', {
        params: {
          include: 'office',
          'fields[simple-offices]': 'code,name,office',
          'fields[offices]':
            'upline_histories,downline_histories,bank_account_number,full_address,cellphone,is_active,is_expired,is_promoted,promoted_by',
          'filter[office_category_id]': 5,
          'page[limit]': 5,
          'filter[search]': this.filter.search,
          'filter[search_email]': this.filter.email,
          'filter[search_cellphone]': this.filter.cellphone,
          'filter[with_summary]': true,
          ...params,
        },
      });

      if (!err) {
        this.members = res;

        if (res.meta.summary) {
          this.summary = res.meta.summary;
        }

        if (!params) {
          this.currentPage = 1;
        }
      }

      this.loading = false;
    },
    onChangePage(page) {
      this.loadMembers({
        ...page,
        'filter[with_summary]': false,
      });
    },
    onClickDetail(member) {
      this.detailModal.officeId = member.id;
      this.detailModal.visible = true;
    },
    onPromote(member) {
      this.promoteModal.officeId = member.id;
      this.promoteModal.visible = true;
    },
    onPromoted() {
      this.promoteModal.visible = false;

      this.loadMembers();
    },
    async onExport() {
      this.startLoadingExport('Mengirim ke server');

      const [, err] = await this.request(
        `/api/v1/offices/-actions/export-pin`,
        {
          method: 'post',
          params: {
            office_type: 'member',
          },
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });

        this.stopLoadingExport();
      } else {
        this.startLoadingExport('Sedang diproses');
      }
    },
    onExportProgress(e) {
      if (e.type === 'App\\Notifications\\OfficePinExported') {
        if (e.status === 'failed') {
          this.stopLoadingExport();

          this.createAlert({
            data: 'Gagal mengexport mitra usaha',
            status: 'error',
          });
        } else if (e.status === 'success') {
          this.stopLoadingExport();

          downloadFileUrl(e.file_url, 'mitra_usaha.csv', { blank: true });
        }
      }
    },
    removeListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onExportProgress
        );
      }
    },
    startLoadingExport(text) {
      this.loadingExport.text = text;
      this.loadingExport.visible = true;
    },
    stopLoadingExport() {
      this.loadingExport.visible = false;
    },
  },
  created() {
    this.loadJobStatus();
    this.loadMembers();
    this.addListener();
  },
  beforeRouteLeave(to, from, next) {
    this.removeListener();
    next();
  },
};
</script>
